import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { artProxyUrl } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The screens ARE the light - a wash would
// put a floor under the black between them.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'panels',
  family: 'stage',
  danceability: 0.88,
  requiresTier: 0,
  energyLevel: 0.86,
  bpmMin: 110,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.65,
  prefers: { gridStrength: [0.60, 1.0] },
  enabled: true
};

// Panels: thirty-six LED screens hung from winches, each showing one slice
// of the cover, flying between formations - wall, tunnel, chevron, cascade,
// helix, scatter. The cover is cut 6 x 6, one slice per screen: aligned as a
// WALL it reads whole; apart, its fragments are seen in flight. Reading the
// cover is an EVENT, not a state - WALL comes back at least one phrase in
// three and the drop reassembles it on the beat.
//
// WHAT MOVES. Each screen has a formation pose (position + orientation,
// given by the formation function for the slot it holds) and flies from its
// previous pose to it in two beats along a straight line with a smoother-
// step profile: zero velocity AND acceleration at both ends, which is what a
// winch's inertia looks like. On arrival a damped two-degree swing about the
// horizontal axis. On top of that pose: the beat flip of its ROW about the
// same axis, and the bass sway.
//
// WHO GOES WHERE. Screens keep THEIR slice always; in WALL each goes to the
// slot of the slice it displays, so the image recomposes. In every other
// formation the slot is free, and the assignment is the whole question: a
// random one gives crossing trajectories and unreadable chaos. Swarm sorts
// both sets by one spatial key because it has forty thousand points; with
// thirty-six, the exact minimum-cost matching (Hungarian, 36^3 operations)
// is affordable and gives straight non-crossing paths. The cost carries a
// half-weight term toward each screen's HOME slot, so the return to WALL -
// where the assignment is forced - is already short when it comes.

const PITCH = 1.14;              // slot spacing in WALL; the screen is 1.0
const SCREEN = 1.0;
const FRAME_BORDER = 0.04;       // of the screen, the matte black frame
const FRAME_DEPTH = 0.07;
const CELLS = 20;                // LED cells per screen edge (the whole cover: 120)
const LUMINANCE = 0.80;
const RIG_Y = 10.5;              // the winches, out of frame
const FLOOR_Y = -3.7;             // just under the WALL's bottom row: its reflection shows
const FOG_DENSITY = 0.03;
const WALL_FILL = 0.74;          // of the frame height the WALL spans
const CAM_FOV = 50;
const CAM_Y = -3.0;              // low, just above the floor: a slight upward look
const CAM_LOOK_Y = -0.6;
const ORBIT_DEG = 15, ORBIT_PERIOD_S = 70;
const HELIX_PERIOD_S = 90;
const TRANSITION_BEATS = 2;
const DROP_LEG_BEATS = 1;        // scatter in one beat, wall in one beat
const DROP_NOISE_BEATS = 4;
const FLIP_FRACTION = 0.35;      // of a beat, the half-turn
const FLIP_MAX_S = 0.32;
const FLASH_S = 0.040;
const FLASH_WHITE = 0.90;
const SWAY_DEG = 2.0, SWAY_PERIOD_S = 1.7, SWAY_DECAY_S = 1.4;
const IDLE_SWAY_DEG = 0.35;
const WALL_EVERY = 3;            // WALL at least one phrase in three
const DROP_SCORE_MIN = 0.7;
const DROP_STALE_MS = 250;
const DROP_HOLD_BEATS = 2;       // after a drop, phrase changes wait: WALL is read
const FREE_BEAT_S = 0.5;
const GLOW_GAIN = 0.13;
const MIRROR_GAIN = 0.32;
const CABLE_PX = 1.6;

export const FORMATIONS = ['wall', 'tunnel', 'chevron', 'cascade', 'helix', 'scatter'];
const MODES = ['slice', 'invert', 'solid', 'offset'];
// Weights of the next formation when WALL is not forced (scatter is rare).
const FORMATION_WEIGHTS = { wall: 0.30, tunnel: 0.22, chevron: 0.18, cascade: 0.18, helix: 0.20, scatter: 0.07 };

const TIER_LAYOUT = {
  0: { grid: 4, fog: false, mirror: false },
  1: { grid: 6, fog: true, mirror: false },
  2: { grid: 6, fog: true, mirror: true }
};

const _size = new THREE.Vector2();
const _v = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3();
const _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion();
const _m = new THREE.Matrix4(), _m2 = new THREE.Matrix4();
const _e = new THREE.Euler();
const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
const _mirror = new THREE.Matrix4().makeScale(1, -1, 1);
const _toFloor = new THREE.Matrix4().makeTranslation(0, -FLOOR_Y, 0);
const _fromFloor = new THREE.Matrix4().makeTranslation(0, FLOOR_Y, 0);

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function smoother(u) { u = Math.min(1, Math.max(0, u)); return u * u * u * (u * (u * 6 - 15) + 10); }
// Ease-out with a small overshoot (about 5%): the flip's inertia.
function backOut(u) { u = Math.min(1, Math.max(0, u)); const c1 = 0.9, c3 = c1 + 1; const x = u - 1; return 1 + c3 * x * x * x + c1 * x * x; }
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================================================
// Formations - pure functions of (slot, grid, time, seed). Exported for the
// acceptance tests. `out` receives position (x, y, z) and orientation as a
// yaw / pitch / roll triple (radians, applied Y then X then Z) with the face
// normal along local +z. Slot s has column s % G and row floor(s / G), row 0
// at the TOP in WALL.
// ============================================================================
export function poseFormation(name, G, s, t, seed, out) {
  const N = G * G;
  const c = s % G, r = Math.floor(s / G);
  const half = (G - 1) / 2;
  out.yaw = 0; out.pitch = 0; out.roll = 0;
  if (name === 'wall') {
    out.x = (c - half) * PITCH; out.y = (half - r) * PITCH; out.z = 0;
    return out;
  }
  if (name === 'tunnel') {
    // One ring per row, turned to the axis; alternate rings offset half a
    // step so the tube reads as brickwork rather than as columns.
    // The axis follows the camera's line of sight (it looks slightly up),
    // and the near ring is close enough to frame the view: a tube one looks
    // straight into.
    const radius = 0.45 * G;
    const camR = cameraRadius(G);
    const slope = (CAM_LOOK_Y - CAM_Y) / camR;
    out.z = 4.2 - r * 2.7;
    // Lifted above the line of sight so the near ring clears the floor.
    const axisY = CAM_Y + 1.3 + (camR - out.z) * slope;
    const theta = (c + 0.5 * (r % 2)) * (Math.PI * 2 / G) + Math.PI / 2;
    out.x = radius * Math.cos(theta); out.y = axisY + radius * Math.sin(theta);
    // Local +z toward the axis, local +y toward the camera (image top toward
    // the viewer, as in a tube of pictures).
    out._lookAt = { x: 0, y: axisY, z: out.z, upX: 0, upY: 0, upZ: 1 };
    return out;
  }
  if (name === 'chevron') {
    // Two arms of G along x G/2 high, from an apex at the back to the sides
    // in front; inner faces toward the camera.
    const perArm = N / 2;
    const arm = s < perArm ? -1 : 1;
    const j = s % perArm;
    const a = j % G, h = Math.floor(j / G);
    const u = G > 1 ? a / (G - 1) : 0;
    const x0 = 0.9, x1 = 0.8 * G;
    const z0 = -0.75 * G, z1 = 1.0;
    out.x = arm * (x0 + (x1 - x0) * u);
    out.z = z0 + (z1 - z0) * u;
    out.y = (h - (G / 2 - 1) / 2) * PITCH * 1.05 + 0.3;
    const dx = arm * (x1 - x0), dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    // Normal perpendicular to the arm, toward the inside and the camera.
    const nx = -arm * Math.abs(dz) / len, nz = Math.abs(dx) / len;
    out.yaw = Math.atan2(nx, nz);
    return out;
  }
  if (name === 'cascade') {
    // A diagonal staircase: each row further back and higher, shifted aside.
    out.x = (c - half) * PITCH + (r - half) * 0.55;
    out.y = (r - half) * 0.78 + 0.3;
    out.z = 1.6 - r * 1.6;
    out.pitch = -0.14;
    return out;
  }
  if (name === 'helix') {
    const turns = 2.5;
    const radius = 0.55 * G;
    const theta = s * (Math.PI * 2 * turns / N) + (Math.PI * 2 / HELIX_PERIOD_S) * t;
    out.x = radius * Math.cos(theta); out.z = radius * Math.sin(theta);
    out.y = -2.9 + 6.8 * (N > 1 ? s / (N - 1) : 0.5);
    // Facing outward, upright.
    out.yaw = Math.atan2(Math.cos(theta), Math.sin(theta));
    return out;
  }
  // scatter: seeded positions in a volume, kept a screen and a half apart,
  // orientations random but never edge-on to the camera.
  const p = scatterSet(G, seed)[s];
  out.x = p.x; out.y = p.y; out.z = p.z; out.yaw = p.yaw; out.pitch = p.pitch; out.roll = p.roll;
  return out;
}

// The scatter set is drawn by rejection sampling, so it is generated once
// per (grid, seed) and cached: the pose is asked for N times a frame.
let _scatterKey = null, _scatterPts = null;
function scatterSet(G, seed) {
  const key = G + ':' + (seed | 0);
  if (key === _scatterKey) return _scatterPts;
  const N = G * G;
  const rnd = mulberry32((seed | 0) * 7919 + 17);
  const pts = [];
  const minD = 1.9;
  for (let i = 0; i < N; i++) {
    let px = 0, py = 0, pz = 0, tries = 0;
    for (; tries < 200; tries++) {
      px = (rnd() * 2 - 1) * 5.8; py = -2.6 + rnd() * 6.4; pz = -7.0 + rnd() * 9.5;
      let ok = true;
      for (let k = 0; k < pts.length; k++) {
        const o = pts[k];
        if (Math.hypot(o.x - px, o.y - py, o.z - pz) < minD) { ok = false; break; }
      }
      if (ok) break;
    }
    pts.push({ x: px, y: py, z: pz, yaw: (rnd() * 2 - 1) * 1.4, pitch: (rnd() * 2 - 1) * 0.7, roll: (rnd() * 2 - 1) * 0.5 });
  }
  _scatterKey = key; _scatterPts = pts;
  return pts;
}

// The camera's distance to the wall's centre, for a WALL of G rows filling
// WALL_FILL of the frame's height.
function cameraRadius(G) {
  return (G * PITCH / WALL_FILL) / (2 * Math.tan(THREE.MathUtils.degToRad(CAM_FOV) / 2));
}

// Minimum-cost assignment (Hungarian, O(n^3)). cost is row-major n x n:
// cost[i * n + j] is screen i taking slot j. Returns slot per screen.
export function hongrois(cost, n) {
  const INF = 1e18;
  const u = new Float64Array(n + 1), v = new Float64Array(n + 1);
  const p = new Int32Array(n + 1), way = new Int32Array(n + 1);
  const minv = new Float64Array(n + 1);
  const used = new Uint8Array(n + 1);
  for (let i = 1; i <= n; i++) {
    p[0] = i;
    let j0 = 0;
    minv.fill(INF); used.fill(0);
    do {
      used[j0] = 1;
      const i0 = p[j0];
      let delta = INF, j1 = 0;
      for (let j = 1; j <= n; j++) {
        if (used[j]) continue;
        const cur = cost[(i0 - 1) * n + (j - 1)] - u[i0] - v[j];
        if (cur < minv[j]) { minv[j] = cur; way[j] = j0; }
        if (minv[j] < delta) { delta = minv[j]; j1 = j; }
      }
      for (let j = 0; j <= n; j++) {
        if (used[j]) { u[p[j]] += delta; v[j] -= delta; } else minv[j] -= delta;
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do { const j1 = way[j0]; p[j0] = p[j1]; j0 = j1; } while (j0);
  }
  const slot = new Int32Array(n);
  for (let j = 1; j <= n; j++) if (p[j] > 0) slot[p[j] - 1] = j - 1;
  return slot;
}

// Which slot each screen takes in `name`: forced in WALL (the slot of the
// slice it displays), the Hungarian matching elsewhere. `from` holds the
// screens' current positions (x, y, z interleaved), `perm` the displayed
// slice of each screen. Exported for the tests.
export function planifier(name, G, from, perm, t, seed) {
  const N = G * G;
  const slot = new Int32Array(N);
  if (name === 'wall') {
    for (let i = 0; i < N; i++) slot[i] = perm[i];
    return slot;
  }
  const o = {};
  const target = new Float32Array(N * 3), home = new Float32Array(N * 3);
  for (let s = 0; s < N; s++) {
    poseFormation(name, G, s, t, seed, o);
    target[s * 3] = o.x; target[s * 3 + 1] = o.y; target[s * 3 + 2] = o.z;
  }
  for (let i = 0; i < N; i++) {
    poseFormation('wall', G, perm[i], 0, 0, o);
    home[i * 3] = o.x; home[i * 3 + 1] = o.y; home[i * 3 + 2] = o.z;
  }
  const cost = new Float64Array(N * N);
  for (let i = 0; i < N; i++) {
    for (let s = 0; s < N; s++) {
      const d = Math.hypot(from[i * 3] - target[s * 3], from[i * 3 + 1] - target[s * 3 + 1], from[i * 3 + 2] - target[s * 3 + 2]);
      const dh = Math.hypot(home[i * 3] - target[s * 3], home[i * 3 + 1] - target[s * 3 + 1], home[i * 3 + 2] - target[s * 3 + 2]);
      cost[i * N + s] = d + 0.5 * dh;
    }
  }
  return hongrois(cost, N);
}

// ============================================================================
// Shaders. mediump throughout: every hash input is bounded (mod 61) and
// nothing derived from time reaches a hash - see the fp16 note on the TV.
// ============================================================================
const SAFE_FLOOR = 0.5;
const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
// FogExp2 by hand, the same law as scene.fog so the built-in floor matches.
const FOG_CHUNK = `
  uniform float uFog;
  float fogAt(float depth) { float k = uFog * depth; return exp(-k * k); }
`;
// Common vertex prologue: world / view / clip, fog, safe zone, the mirror
// fade (uMirror > 0.5 draws the floor reflection: below the floor, fading
// with depth under it).
const COMMON_VERTEX = `
  uniform float uMirror;
  uniform float uFloorY;
  varying float vGain;
  ${SAFE_CHUNK}
  ${FOG_CHUNK}
  vec4 commonTransform(vec3 pos, out vec4 world, out vec4 mv) {
    world = modelMatrix * instanceMatrix * vec4(pos, 1.0);
    mv = viewMatrix * world;
    vec4 clip = projectionMatrix * mv;
    float fade = uMirror > 0.5 ? ${MIRROR_GAIN.toFixed(2)} * smoothstep(uFloorY - 3.5, uFloorY, world.y) : 1.0;
    vGain = safeAt(clip) * fogAt(-mv.z) * fade;
    return clip;
  }
`;

const faceVertexShader = `
  precision mediump float;
  attribute vec2 aSlice;
  attribute vec3 aSolid;
  uniform float uProjScale;    // pixels per world unit at depth 1
  varying vec2 vUv;
  varying vec2 vSlice;
  varying vec3 vSolid;
  varying float vPx;           // device pixels per LED cell
  ${COMMON_VERTEX}
  void main() {
    vUv = uv; vSlice = aSlice; vSolid = aSolid;
    vec4 world, mv;
    gl_Position = commonTransform(position, world, mv);
    vPx = (${(SCREEN / CELLS).toFixed(5)}) * uProjScale / max(-mv.z, 0.1);
  }
`;
const faceFragmentShader = `
  precision mediump float;
  uniform sampler2D uArt;
  uniform float uGrid;         // 6 or 4: slices per edge
  uniform float uInvert;
  uniform float uSolid;
  uniform float uNoise;
  uniform float uNoiseSeed;    // bounded on the CPU (mod 61)
  uniform float uFlash;
  uniform float uTrame;        // the LED mask's visibility
  varying vec2 vUv;
  varying vec2 vSlice;
  varying vec3 vSolid;
  varying float vPx;
  varying float vGain;
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  void main() {
    float C = ${CELLS.toFixed(1)};
    vec2 cc = vUv * C;
    vec2 cell = floor(cc);
    vec2 f = fract(cc);
    vec2 off = vec2(vSlice.x, uGrid - 1.0 - vSlice.y);
    // Sampled at the cell's centre so the picture is quantised to the pitch;
    // far away (under two pixels a cell) the smooth sample takes over or
    // the quantisation aliases.
    vec2 uvCell = ((cell + 0.5) / C + off) / uGrid;
    vec2 uvSmooth = (vUv + off) / uGrid;
    float near = smoothstep(4.5, 7.0, vPx);
    vec3 col = mix(texture2D(uArt, uvSmooth).rgb, texture2D(uArt, uvCell).rgb, near);
    col = mix(col, 1.0 - col, uInvert);
    col = mix(col, vSolid, uSolid);
    // Video noise: a grey snow with rolling bands, per cell.
    vec2 nc = mod(cell + vec2(uNoiseSeed, uNoiseSeed * 0.37), 61.0);
    float n = hash12(nc);
    float band = 0.7 + 0.3 * hash12(vec2(mod(cell.y + uNoiseSeed * 3.0, 61.0), 7.0));
    col = mix(col, vec3(n * band), uNoise);
    // The LED mask: a COSINE dip between cells - no edge sharper than the
    // cell itself, so it does not moire - and only when a cell spans at
    // least five pixels. Brightness compensated.
    float gx = 0.5 - 0.5 * cos(f.x * 6.2832), gy = 0.5 - 0.5 * cos(f.y * 6.2832);
    float m = pow(gx * gy, 0.45);
    float trame = uTrame * smoothstep(3.0, 6.0, vPx);
    col *= mix(1.0, m * 1.2, trame);
    col = mix(col, vec3(${FLASH_WHITE.toFixed(2)}), uFlash);
    gl_FragColor = vec4(col * ${LUMINANCE.toFixed(2)} * vGain, 1.0);
  }
`;

// The frame: a box. Its front and sides are matte black; its back is the
// screen's back - a dark shell with a fixing plate, two cable channels and a
// status LED - seen when a row flips.
const frameVertexShader = `
  precision mediump float;
  attribute float aSeed;
  varying vec2 vUv;
  varying vec3 vN;
  varying float vSeed;
  ${COMMON_VERTEX}
  void main() {
    vUv = uv; vN = normal; vSeed = aSeed;
    vec4 world, mv;
    gl_Position = commonTransform(position, world, mv);
  }
`;
const frameFragmentShader = `
  precision mediump float;
  uniform float uTime;         // bounded on the CPU (mod 64)
  varying vec2 vUv;
  varying vec3 vN;
  varying float vSeed;
  varying float vGain;
  void main() {
    vec3 col = vec3(0.012);
    if (vN.z < -0.5) {
      // The back.
      col = vec3(0.045, 0.045, 0.05);
      vec2 d = abs(vUv - 0.5);
      float plate = 1.0 - smoothstep(0.20, 0.215, max(d.x, d.y));
      col = mix(col, vec3(0.10, 0.10, 0.105), plate);
      float rim = smoothstep(0.17, 0.18, max(d.x, d.y)) * plate;
      col = mix(col, vec3(0.05), rim * 0.7);
      // Two cable channels running up to the top edge.
      float ch = min(abs(vUv.x - 0.30), abs(vUv.x - 0.70));
      float channel = (1.0 - smoothstep(0.012, 0.022, ch)) * step(0.5, vUv.y);
      col = mix(col, vec3(0.03), channel);
      // Four screws at the plate's corners.
      vec2 sc = abs(d - 0.17);
      float screw = 1.0 - smoothstep(0.010, 0.016, length(sc));
      col = mix(col, vec3(0.22), screw);
      // The status LED, blinking at its own pace.
      float blink = step(0.5, fract(uTime * (0.6 + vSeed * 0.5) + vSeed));
      float led = 1.0 - smoothstep(0.010, 0.016, length(vUv - vec2(0.5, 0.21)));
      col = mix(col, vec3(0.1, 0.9, 0.25) * (0.5 + 0.5 * blink), led);
    } else if (abs(vN.z) < 0.5) {
      col = vec3(0.02);
    }
    gl_FragColor = vec4(col * vGain, 1.0);
  }
`;

// Cables: thin quads yawed to the camera, lit from below by the screen.
const cableVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  ${COMMON_VERTEX}
  void main() {
    vUv = uv;
    vec4 world, mv;
    gl_Position = commonTransform(position, world, mv);
  }
`;
const cableFragmentShader = `
  precision mediump float;
  varying vec2 vUv;
  varying float vGain;
  void main() {
    float a = smoothstep(0.0, 0.4, vUv.x) * smoothstep(1.0, 0.6, vUv.x);
    float lit = mix(0.42, 0.14, smoothstep(0.0, 0.35, vUv.y));
    gl_FragColor = vec4(vec3(lit) * vGain * a, a);
  }
`;

// The glow in the haze in front of each screen: a soft disc in the slice's
// mean colour, additive, facing-weighted on the CPU (aGlow.w).
const glowVertexShader = `
  precision mediump float;
  attribute vec4 aGlow;
  varying vec2 vUv;
  varying vec4 vGlow;
  ${COMMON_VERTEX}
  void main() {
    vUv = uv; vGlow = aGlow;
    vec4 world, mv;
    gl_Position = commonTransform(position, world, mv);
  }
`;
const glowFragmentShader = `
  precision mediump float;
  uniform float uGlowGain;
  varying vec2 vUv;
  varying vec4 vGlow;
  varying float vGain;
  void main() {
    vec2 d = vUv - 0.5;
    float g = exp(-dot(d, d) * 9.0) * (1.0 - smoothstep(0.3, 0.5, length(d)));
    gl_FragColor = vec4(vGlow.rgb * g * vGlow.w * uGlowGain * vGain, 1.0);
  }
`;

const floorVertexShader = `
  precision mediump float;
  varying vec3 vWorld;
  varying float vFog;
  ${FOG_CHUNK}
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    vec4 mv = viewMatrix * world;
    vFog = fogAt(-mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;
const floorFragmentShader = `
  precision mediump float;
  varying vec3 vWorld;
  varying float vFog;
  void main() {
    float d = length(vWorld.xz);
    vec3 col = vec3(0.010, 0.010, 0.012) + vec3(0.016) * exp(-d / 9.0);
    gl_FragColor = vec4(col * vFog, 1.0);
  }
`;

// ============================================================================
export class PanelsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER_LAYOUT[this._tier] || TIER_LAYOUT[1];
    this._G = this._layout.grid;
    this._N = this._G * this._G;
    const form = urlParam('panelsform');
    this._forceForm = FORMATIONS.includes(form) ? form : null;
    const mode = urlParam('panelsmode');
    this._forceMode = MODES.includes(mode) ? mode : null;
    this._debugDrop = urlParam('panelsdrop') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const N = this._N;
    this._time = 0;
    this._beatS = FREE_BEAT_S;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._beatPhase = 0;
    this._flashAt = -1e9;
    this._flipRow = -1; this._prevFlipRow = -1; this._flipAt = -1e9;
    this._formation = 'wall'; this._sinceWall = 0; this._scatterSeed = 1;
    this._mode = this._forceMode || 'slice';
    this._perm = new Int32Array(N); for (let i = 0; i < N; i++) this._perm[i] = i;
    this._slot = new Int32Array(N); for (let i = 0; i < N; i++) this._slot[i] = i;
    this._cur = new Float32Array(N * 3); this._curQ = new Float32Array(N * 4);
    this._from = new Float32Array(N * 3); this._fromQ = new Float32Array(N * 4);
    this._transT0 = -1e9; this._transDur = 1; this._transByDrop = false; this._transU = 1;
    this._arrived = true;
    this._swayAt = new Float32Array(N).fill(-1e9);
    this._swayPhase = new Float32Array(N); for (let i = 0; i < N; i++) this._swayPhase[i] = Math.random() * Math.PI * 2;
    this._noise = false; this._dropStage = 0; this._lastDropAt = -1e9; this._holdUntil = -1e9;
    this._drops = []; this._dropIdx = 0; this._marksRef = undefined; this._lastNowMs = null; this._debugDropAt = 6;
    this._lastArtworkUrl = undefined; this._lastPalette = null;
    this._objects = []; this._geometries = []; this._materials = []; this._textures = [];
    this._low = 0.5; this._high = 0.5;

    this._previousFog = scene.fog;
    scene.fog = this._layout.fog ? new THREE.FogExp2(0x000000, FOG_DENSITY) : null;
    this._fogValue = this._layout.fog ? FOG_DENSITY : 0;

    camera.fov = CAM_FOV;
    camera.updateProjectionMatrix();
    this._camR = cameraRadius(this._G);
    this._placeCamera();

    this._buildTexture();
    this._buildMeshes();
    this._applyPalette(null);
    this._writeSlices();
    // The opening pose: WALL, settled, and the first pose snapshot.
    for (let i = 0; i < N; i++) this._formationPose(this._formation, this._slot[i], 0, _v, _q), this._storeCur(i, _v, _q);
    if (this._forceForm && this._forceForm !== 'wall') this._launch(this._forceForm, 0.01);
  }

  _placeCamera() {
    const c = this._camera, t = this._time || 0;
    const a = THREE.MathUtils.degToRad(ORBIT_DEG) * Math.sin(t * Math.PI * 2 / ORBIT_PERIOD_S);
    c.position.set(Math.sin(a) * this._camR, CAM_Y, Math.cos(a) * this._camR);
    c.lookAt(0, CAM_LOOK_Y, 0);
    c.updateMatrixWorld();
  }

  // -------------------------------------------------------------- texture --
  // A placeholder until the cover arrives: the palette as a soft gradient,
  // so the wall is never black.
  _buildTexture() {
    const cv = document.createElement('canvas');
    cv.width = 64; cv.height = 64;
    const ctx = cv.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 64, 64);
    g.addColorStop(0, '#3a2a6a'); g.addColorStop(1, '#2a6a7a');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    this._artTex = tex;
    this._textures.push(tex);
    this._avg = new Float32Array(this._N * 3).fill(0.3);
  }

  _loadArtwork(url) {
    const self = this;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      if (!self._scene) return;
      const tex = new THREE.Texture(img);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.generateMipmaps = true;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.anisotropy = 1;
      tex.needsUpdate = true;
      self._artTex = tex;
      self._textures.push(tex);
      if (self._faceU) self._faceU.uArt.value = tex;
      if (self._mirrorFaceU) self._mirrorFaceU.uArt.value = tex;
      // The slices' mean colours, for the glow in the haze: the image drawn
      // at G x G is exactly those means.
      try {
        const G = self._G;
        const cv = document.createElement('canvas');
        cv.width = G; cv.height = G;
        const ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0, G, G);
        const px = ctx.getImageData(0, 0, G, G).data;
        for (let k = 0; k < G * G; k++) {
          for (let ch = 0; ch < 3; ch++) self._avg[k * 3 + ch] = Math.pow(px[k * 4 + ch] / 255, 2.2);
        }
      } catch (e) { /* tainted canvas: keep the previous means */ }
    };
    img.src = artProxyUrl(url);
  }

  // --------------------------------------------------------------- meshes --
  _baseUniforms(mirror) {
    return {
      uMirror: { value: mirror ? 1 : 0 },
      uFloorY: { value: FLOOR_Y },
      uFog: { value: this._fogValue },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
  }

  _faceUniforms(mirror) {
    return Object.assign(this._baseUniforms(mirror), {
      uArt: { value: this._artTex }, uGrid: { value: this._G }, uProjScale: { value: 500 },
      uInvert: { value: 0 }, uSolid: { value: 0 }, uNoise: { value: 0 }, uNoiseSeed: { value: 0 },
      uFlash: { value: 0 }, uTrame: { value: 0.5 }
    });
  }

  _buildMeshes() {
    const N = this._N;
    // Faces: a plane just in front of the frame's front.
    const faceGeo = new THREE.PlaneGeometry(SCREEN, SCREEN);
    faceGeo.translate(0, 0, FRAME_DEPTH / 2 + 0.004);
    this._sliceAttr = new THREE.InstancedBufferAttribute(new Float32Array(N * 2), 2);
    this._solidAttr = new THREE.InstancedBufferAttribute(new Float32Array(N * 3), 3);
    faceGeo.setAttribute('aSlice', this._sliceAttr);
    faceGeo.setAttribute('aSolid', this._solidAttr);
    this._faceU = this._faceUniforms(false);
    const faceMat = new THREE.ShaderMaterial({ uniforms: this._faceU, vertexShader: faceVertexShader, fragmentShader: faceFragmentShader, side: THREE.FrontSide });
    this._faces = this._instanced(faceGeo, faceMat, N, 10);
    // Frames: the box body.
    const frameGeo = new THREE.BoxGeometry(SCREEN * (1 + 2 * FRAME_BORDER), SCREEN * (1 + 2 * FRAME_BORDER), FRAME_DEPTH);
    const seeds = new Float32Array(N); for (let i = 0; i < N; i++) seeds[i] = Math.random();
    frameGeo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    this._frameU = Object.assign(this._baseUniforms(false), { uTime: { value: 0 } });
    const frameMat = new THREE.ShaderMaterial({ uniforms: this._frameU, vertexShader: frameVertexShader, fragmentShader: frameFragmentShader, side: THREE.FrontSide });
    this._frames = this._instanced(frameGeo, frameMat, N, 9);
    // Cables: two per screen.
    const cableGeo = new THREE.PlaneGeometry(1, 1);
    cableGeo.translate(0, 0.5, 0);   // origin at the bottom: scale y is the length
    this._cableU = this._baseUniforms(false);
    const cableMat = new THREE.ShaderMaterial({ uniforms: this._cableU, vertexShader: cableVertexShader, fragmentShader: cableFragmentShader, transparent: true, depthWrite: false, side: THREE.DoubleSide });
    this._cables = this._instanced(cableGeo, cableMat, N * 2, 8);
    // Glows in the haze.
    if (this._layout.fog) {
      const glowGeo = new THREE.PlaneGeometry(SCREEN * 2.4, SCREEN * 2.4);
      this._glowAttr = new THREE.InstancedBufferAttribute(new Float32Array(N * 4), 4);
      glowGeo.setAttribute('aGlow', this._glowAttr);
      this._glowU = Object.assign(this._baseUniforms(false), { uGlowGain: { value: GLOW_GAIN } });
      const glowMat = new THREE.ShaderMaterial({ uniforms: this._glowU, vertexShader: glowVertexShader, fragmentShader: glowFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
      this._glows = this._instanced(glowGeo, glowMat, N, 11);
    }
    // The floor, and at tier 2 its reflection: the faces and frames drawn
    // again under the floor, mirrored, dimmed, fading with depth below it.
    const floorGeo = new THREE.PlaneGeometry(90, 90);
    floorGeo.rotateX(-Math.PI / 2);
    // No depth write and drawn first: the reflection is the mirrored meshes
    // drawn UNDER the floor, and a floor that wrote depth would hide them.
    const floorMat = new THREE.ShaderMaterial({ uniforms: { uFog: { value: this._fogValue } }, vertexShader: floorVertexShader, fragmentShader: floorFragmentShader, depthWrite: false });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.position.y = FLOOR_Y;
    floor.renderOrder = 0;
    this._objects.push(floor); this._geometries.push(floorGeo); this._materials.push(floorMat);
    this._scene.add(floor);
    if (this._layout.mirror) {
      this._mirrorFaceU = this._faceUniforms(true);
      const mf = new THREE.ShaderMaterial({ uniforms: this._mirrorFaceU, vertexShader: faceVertexShader, fragmentShader: faceFragmentShader, side: THREE.BackSide });
      this._mirrorFaces = this._instanced(faceGeo, mf, N, 5);
      this._mirrorFrameU = Object.assign(this._baseUniforms(true), { uTime: this._frameU.uTime });
      const mfr = new THREE.ShaderMaterial({ uniforms: this._mirrorFrameU, vertexShader: frameVertexShader, fragmentShader: frameFragmentShader, side: THREE.BackSide });
      this._mirrorFrames = this._instanced(frameGeo, mfr, N, 4);
    }
    const self = this;
    this._faces.onBeforeRender = function (renderer, scene, camera) {
      renderer.getDrawingBufferSize(_size);
      const ps = _size.y / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
      self._faceU.uProjScale.value = ps;
      if (self._mirrorFaceU) self._mirrorFaceU.uProjScale.value = ps;
      self._projScale = ps;
    };
  }

  _instanced(geo, mat, count, order) {
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    this._objects.push(mesh); this._materials.push(mat);
    if (!this._geometries.includes(geo)) this._geometries.push(geo);
    this._scene.add(mesh);
    return mesh;
  }

  // -------------------------------------------------------------- palette --
  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length >= 2 ? vivid : (vivid.length + dominant.length >= 2 ? vivid.concat(dominant) : ['#ff4fa0', '#3fb6ff', '#ffd23f']);
    const cols = src.slice(0, 3).map((h) => {
      _col.set(h); _col.getHSL(_hsl);
      _col.setHSL(_hsl.h, Math.max(0.55, _hsl.s), THREE.MathUtils.clamp(_hsl.l, 0.42, 0.6));
      return [_col.r, _col.g, _col.b];
    });
    this._paletteLinear = cols;
    this._writeSolid();
  }

  // SOLID: one palette colour per screen in a checkerboard (by slice).
  _writeSolid() {
    const G = this._G, a = this._solidAttr.array, cols = this._paletteLinear;
    for (let i = 0; i < this._N; i++) {
      const c = i % G, r = Math.floor(i / G);
      const k = (c + r) % Math.max(2, cols.length);
      const col = cols[k % cols.length];
      a[i * 3] = col[0]; a[i * 3 + 1] = col[1]; a[i * 3 + 2] = col[2];
    }
    this._solidAttr.needsUpdate = true;
  }

  _writeSlices() {
    const G = this._G, a = this._sliceAttr.array;
    for (let i = 0; i < this._N; i++) {
      const s = this._perm[i];
      a[i * 2] = s % G; a[i * 2 + 1] = Math.floor(s / G);
    }
    this._sliceAttr.needsUpdate = true;
  }

  onTrackChange(profile) { this._applyPalette(profile); }

  // ----------------------------------------------------------- scheduling --
  // Seconds until the next strong drop, from the analysis marks (Shatter's
  // pointer: fires on the first frame at or after the mark, never skips).
  _updateDropSchedule(profile) {
    if (this._debugDrop) {
      if (this._time > this._debugDropAt + 0.5) this._debugDropAt = this._time + 32 * this._beatS;
      return this._debugDropAt - this._time;
    }
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks;
      this._drops = [];
      if (marks) for (let i = 0; i < marks.length; i++) if (marks[i].type === 'drop' && marks[i].score >= DROP_SCORE_MIN) this._drops.push(marks[i].atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return null;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    const ref = this._lastNowMs != null ? Math.min(this._lastNowMs, now) : now;
    this._lastNowMs = now;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] < ref - DROP_STALE_MS) this._dropIdx++;
    if (this._dropIdx >= this._drops.length) return null;
    return (this._drops[this._dropIdx] - now) / 1000;
  }

  _pickFormation() {
    if (this._sinceWall >= WALL_EVERY - 1) return 'wall';
    let total = 0;
    const names = FORMATIONS.filter((n) => n !== this._formation);
    for (const n of names) total += FORMATION_WEIGHTS[n];
    let r = Math.random() * total;
    for (const n of names) { r -= FORMATION_WEIGHTS[n]; if (r <= 0) return n; }
    return names[names.length - 1];
  }

  _pickMode(nextForm) {
    const r = Math.random();
    if (nextForm === 'wall') return r < 0.55 ? 'slice' : (r < 0.85 ? 'offset' : 'invert');
    return r < 0.50 ? 'slice' : (r < 0.72 ? 'invert' : 'solid');
  }

  _setMode(mode) {
    this._mode = mode;
    const N = this._N;
    if (mode === 'offset') {
      // A derangement: a cyclic shift of the slices by a random non-zero
      // amount, so no screen shows its own slice.
      const shift = 1 + Math.floor(Math.random() * (N - 1));
      for (let i = 0; i < N; i++) this._perm[i] = (i + shift) % N;
    } else {
      for (let i = 0; i < N; i++) this._perm[i] = i;
    }
    this._writeSlices();
  }

  // Launch a flight to `name`: snapshot the current poses as the start, plan
  // the slots, set the clock. `dur` in seconds; byDrop reads its progress
  // from the drop schedule instead.
  _launch(name, dur, byDrop) {
    const N = this._N;
    if (name === 'scatter') this._scatterSeed = (this._scatterSeed * 1103515245 + 12345) & 0x7fffffff;
    for (let i = 0; i < N * 3; i++) this._from[i] = this._cur[i];
    for (let i = 0; i < N * 4; i++) this._fromQ[i] = this._curQ[i];
    this._slot = planifier(name, this._G, this._from, this._perm, this._time, this._scatterSeed);
    this._formation = name;
    this._transT0 = this._time; this._transDur = Math.max(0.01, dur); this._transByDrop = !!byDrop;
    this._arrived = false;
  }

  // ---------------------------------------------------------------- update --
  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (paused) return;                       // gel: the frame holds as it is
    const director = profile.director || {};
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const t = this._time;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
    }
    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }

    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : 0;
    this._beatS = bpm ? 60 / bpm : FREE_BEAT_S;
    const beatS = this._beatS;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || {};
    const low = measured ? (bands.low || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    this._low += (low - this._low) * Math.min(1, dt * 8);
    this._high += (high - this._high) * Math.min(1, dt * 8);

    // --- clocks: beat, bar, phrase edges
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
      this._beatPhase = profile.beatPhase;
    } else {
      if (t - this._freeBeatAt >= beatS) { this._freeBeatAt = t; beatEdge = true; }
      this._beatPhase = Math.min(1, (t - this._freeBeatAt) / beatS);
    }
    if (beatEdge) this._beatCount++;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = profile.barPhase;
    } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
    // The phrase: phraseIndex when it moves; otherwise (no grid, or the
    // landing page's phraseIndex frozen at 0 - Swarm's lesson) every
    // sixteen beats of our own count.
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (beatEdge) this._beatsSincePhrase = (this._beatsSincePhrase || 0) + 1;
    if (!phraseEdge && beatEdge && this._beatsSincePhrase >= 16) phraseEdge = true;
    if (phraseEdge) this._beatsSincePhrase = 0;
    this._lastPhrase = phrase;

    // --- the drop: noise four beats out, scatter at two, wall at one, the
    // cover whole on the beat. Progress of the last leg is read off the
    // schedule itself, so a re-anchored position cannot land it early or late.
    const sUntil = this._updateDropSchedule(profile);
    if (sUntil == null || sUntil > DROP_NOISE_BEATS * beatS + 0.5) {
      if (this._dropStage > 0) { this._noise = false; if (this._transByDrop) { this._transByDrop = false; this._transT0 = t - this._transU * beatS; this._transDur = beatS; } }
      this._dropStage = 0;
    } else {
      if (this._dropStage === 0 && sUntil <= DROP_NOISE_BEATS * beatS && sUntil > (DROP_LEG_BEATS + 0.2) * beatS) { this._dropStage = 1; this._noise = true; }
      if (this._dropStage === 1 && sUntil <= 2 * DROP_LEG_BEATS * beatS) {
        this._dropStage = 2;
        this._noise = true;
        if (this._mode === 'offset' || this._forceMode == null) this._setMode('slice');
        this._launch('scatter', Math.max(0.05, sUntil - DROP_LEG_BEATS * beatS));
      }
      if (this._dropStage === 2 && sUntil <= DROP_LEG_BEATS * beatS) {
        this._dropStage = 3;
        this._launch('wall', DROP_LEG_BEATS * beatS, true);
      }
      if (this._dropStage === 3 && sUntil <= 0) {
        // Fired. Back to stage 0 at once: until the pointer moves on
        // (DROP_STALE_MS later) sUntil is negative and re-arms nothing.
        this._dropStage = 0;
        this._noise = false;
        this._flashAt = t;
        this._lastDropAt = t;
        this._sinceWall = 0;
        this._holdUntil = t + DROP_HOLD_BEATS * beatS;
        if (this._transByDrop) { this._transByDrop = false; this._transT0 = t - this._transDur; }
      }
    }
    if (this._transByDrop && sUntil != null) this._transU = THREE.MathUtils.clamp(1 - sUntil / (DROP_LEG_BEATS * beatS), 0, 1);

    // --- the phrase: a formation, a content mode
    if (phraseEdge && this._dropStage === 0 && t >= this._holdUntil && !this._forceForm) {
      const next = this._pickFormation();
      if (!this._forceMode) this._setMode(this._pickMode(next));
      this._launch(next, TRANSITION_BEATS * beatS);
      this._sinceWall = next === 'wall' ? 0 : this._sinceWall + 1;
    } else if (phraseEdge && this._forceForm && this._forceForm === this._formation && !this._forceMode) {
      // Pinned formation: the modes still cycle, so the debug view shows them.
      this._setMode(this._pickMode(this._forceForm));
      if (this._forceForm === 'wall') this._launch('wall', TRANSITION_BEATS * beatS);
    }
    // --- the bar: the flash
    if (barEdge) this._flashAt = t;
    // --- the beat: the next row flips
    if (beatEdge) {
      this._prevFlipRow = this._flipRow;
      this._flipRow = (this._flipRow + 1) % this._G;
      this._flipAt = t;
    }

    this._placeCamera();
    this._writeFrame(t, beatS);
  }

  // The formation pose of slot s at time t, into position v and quaternion q.
  _formationPose(name, s, t, v, q) {
    const o = this._poseOut || (this._poseOut = {});
    o._lookAt = null;
    poseFormation(name, this._G, s, t, this._scatterSeed, o);
    v.set(o.x, o.y, o.z);
    if (o._lookAt) {
      const la = o._lookAt;
      _v2.set(la.x, la.y, la.z);
      _v3.set(la.upX, la.upY, la.upZ);
      _m2.lookAt(_v2, v, _v3);           // local +z toward the target
      q.setFromRotationMatrix(_m2);
    } else {
      _e.set(o.pitch, o.yaw, o.roll, 'YXZ');
      q.setFromEuler(_e);
    }
  }

  _storeCur(i, v, q) {
    this._cur[i * 3] = v.x; this._cur[i * 3 + 1] = v.y; this._cur[i * 3 + 2] = v.z;
    this._curQ[i * 4] = q.x; this._curQ[i * 4 + 1] = q.y; this._curQ[i * 4 + 2] = q.z; this._curQ[i * 4 + 3] = q.w;
  }

  _writeFrame(t, beatS) {
    const N = this._N, G = this._G;
    const cam = this._camera.position;
    // Transition progress.
    let u;
    if (this._transByDrop) u = this._transU;
    else u = THREE.MathUtils.clamp((t - this._transT0) / this._transDur, 0, 1);
    const e = smoother(u);
    if (u >= 1 && !this._arrived) {
      this._arrived = true;
      for (let i = 0; i < N; i++) this._swayAt[i] = t;
    }
    // The flip of the active row (0 -> pi) and of the previous one (pi -> 2 pi).
    const flipDur = Math.min(FLIP_MAX_S, FLIP_FRACTION * beatS);
    const flipU = (t - this._flipAt) / flipDur;
    const flipAngle = Math.PI * backOut(flipU);
    const lowAmp = 0.5 + 1.0 * this._low;

    const faceM = this._faces.instanceMatrix.array;
    const frameM = this._frames.instanceMatrix.array;
    const cableM = this._cables.instanceMatrix.array;
    const glowM = this._glows ? this._glows.instanceMatrix.array : null;
    const glowA = this._glowAttr ? this._glowAttr.array : null;
    const mirF = this._mirrorFaces ? this._mirrorFaces.instanceMatrix.array : null;
    const mirR = this._mirrorFrames ? this._mirrorFrames.instanceMatrix.array : null;
    const projScale = this._projScale || 500;
    const solid = this._mode === 'solid' ? 1 : 0, invert = this._mode === 'invert' ? 1 : 0;
    const noise = this._noise ? 1 : 0;

    for (let i = 0; i < N; i++) {
      // Target pose, start pose, interpolation.
      this._formationPose(this._formation, this._slot[i], t, _v, _q);
      if (u < 1) {
        _v2.set(this._from[i * 3], this._from[i * 3 + 1], this._from[i * 3 + 2]);
        _q2.set(this._fromQ[i * 4], this._fromQ[i * 4 + 1], this._fromQ[i * 4 + 2], this._fromQ[i * 4 + 3]);
        _v.lerpVectors(_v2, _v, e);
        _q.slerpQuaternions(_q2, _q, e);
      }
      this._storeCur(i, _v, _q);
      // Cables from the formation pose's top corners, straight up to the rig.
      const hw = SCREEN * (0.5 + FRAME_BORDER);
      for (let side = 0; side < 2; side++) {
        _v3.set(side === 0 ? -hw * 0.9 : hw * 0.9, hw, 0).applyQuaternion(_q).add(_v);
        const len = Math.max(0.1, RIG_Y - _v3.y);
        const depth = Math.max(0.5, Math.hypot(cam.x - _v3.x, cam.y - (_v3.y + len * 0.3), cam.z - _v3.z));
        const w = CABLE_PX * depth / projScale;
        const yaw = Math.atan2(cam.x - _v3.x, cam.z - _v3.z);
        _q2.setFromAxisAngle(_v2.set(0, 1, 0), yaw);
        _m.compose(_v3, _q2, _v2.set(w, len, 1));
        _m.toArray(cableM, (i * 2 + side) * 16);
      }
      // The swing on arrival and the idle sway, then the row's flip - all
      // about the screen's horizontal axis.
      const r = Math.floor(i / G);
      let angle = 0;
      const sinceSway = t - this._swayAt[i];
      if (sinceSway < 6) angle += THREE.MathUtils.degToRad(SWAY_DEG) * lowAmp * Math.exp(-sinceSway / SWAY_DECAY_S) * Math.sin(sinceSway * Math.PI * 2 / SWAY_PERIOD_S);
      angle += THREE.MathUtils.degToRad(IDLE_SWAY_DEG) * lowAmp * Math.sin(t * 1.3 + this._swayPhase[i]);
      if (r === this._flipRow) angle += flipAngle;
      else if (r === this._prevFlipRow) angle += Math.PI + flipAngle;
      if (angle !== 0) { _q2.setFromAxisAngle(_v2.set(1, 0, 0), angle); _q.multiply(_q2); }
      _m.compose(_v, _q, _v2.set(1, 1, 1));
      _m.toArray(faceM, i * 16);
      _m.toArray(frameM, i * 16);
      if (mirF) {
        _m2.copy(_fromFloor).multiply(_mirror).multiply(_toFloor).multiply(_m);
        _m2.toArray(mirF, i * 16);
        _m2.toArray(mirR, i * 16);
      }
      if (glowM) {
        // The glow sits in front of the face, along its normal, and only
        // lights the haze toward the viewer.
        _v3.set(0, 0, 1).applyQuaternion(_q);
        _v2.copy(_v).addScaledVector(_v3, 0.45);
        _m2.compose(_v2, _q, _v.set(1, 1, 1));
        _m2.toArray(glowM, i * 16);
        _v.subVectors(cam, _v2).normalize();
        const facing = Math.max(0, _v3.dot(_v));
        const k = this._perm[i];
        let cr = this._avg[k * 3], cg = this._avg[k * 3 + 1], cb = this._avg[k * 3 + 2];
        if (invert) { cr = 1 - cr; cg = 1 - cg; cb = 1 - cb; }
        if (solid) { cr = this._solidAttr.array[i * 3]; cg = this._solidAttr.array[i * 3 + 1]; cb = this._solidAttr.array[i * 3 + 2]; }
        if (noise) { cr = cg = cb = 0.45; }
        const flash = t - this._flashAt < FLASH_S ? 1 : 0;
        if (flash) { cr = cg = cb = 1; }
        const o = i * 4;
        glowA[o] = cr; glowA[o + 1] = cg; glowA[o + 2] = cb; glowA[o + 3] = facing;
      }
    }
    this._faces.instanceMatrix.needsUpdate = true;
    this._frames.instanceMatrix.needsUpdate = true;
    this._cables.instanceMatrix.needsUpdate = true;
    if (this._glows) { this._glows.instanceMatrix.needsUpdate = true; this._glowAttr.needsUpdate = true; }
    if (this._mirrorFaces) { this._mirrorFaces.instanceMatrix.needsUpdate = true; this._mirrorFrames.instanceMatrix.needsUpdate = true; }

    // Uniforms: content mode, noise, flash, trame.
    const flash = t - this._flashAt < FLASH_S ? 1 : 0;
    const trame = 0.3 + 0.5 * this._high;
    for (const U of [this._faceU, this._mirrorFaceU]) {
      if (!U) continue;
      U.uInvert.value = invert; U.uSolid.value = solid; U.uNoise.value = noise;
      U.uNoiseSeed.value = Math.floor(t * 30) % 61;
      U.uFlash.value = flash; U.uTrame.value = trame;
    }
    this._frameU.uTime.value = t % 64;
    if (this._glowU) this._glowU.uGlowGain.value = GLOW_GAIN * (0.7 + 0.6 * this._low);
  }

  dispose() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    for (const tx of this._textures) tx.dispose();
    this._objects = []; this._geometries = []; this._materials = []; this._textures = [];
    if (this._scene) this._scene.fog = this._previousFog || null;
    this._scene = null;
  }
}
